import {
  ButtonColor,
  ButtonSize,
  Checkbox,
  Modal,
  ModalType,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Customer, IndividualPageValidation } from 'src/front/api/apiInstance';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import AsyncButton from 'src/front/components/AsyncButton';
import CustomerSearchForm, {
  customerSearchDefaultValues,
  SearchFormData,
} from 'src/front/components/CustomerSearchForm';

import {
  Container,
  Description,
  FormRow,
  Subtitle,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import Markdown from 'src/front/components/Markdown';

import {
  useClearDispatch,
  useCustomerSearchDispatch,
  useCustomerSearchState,
} from 'src/front/contexts/CardEntryContext';

const tableHeaders = [
  'cardEntry_customerTable_name_title',
  'cardEntry_customerInfo_middleName_label',
  'cardEntry_customerTable_birth_title',
  'cardEntry_customerInfo_gender_label',
  'cardEntry_customerTable_document_title',
];

interface Props {
  goToNextStep: (customerId?: string, customerNumber?: string) => void;
  disablePage?: boolean;
}
export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
}

const CustomerSearch: FC<Props> = ({ goToNextStep, disablePage }) => {
  const dispatch = useCustomerSearchDispatch();
  const {
    customerSearchValues,
    pageNumber: initialCustomerPage,
    customerId: selectedCustomerId,
    isNewCustomer: initialIsNewCustomer,
    transferableCardCustomerData,
  } = useCustomerSearchState();
  const clearCustomerSearchState = useClearDispatch(true);
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [page, setPage] = useState<number>(initialCustomerPage || 1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const [warning, setWarning] = useState<string>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow>();
  const [selectedCustomerPage, setSelectedCustomerPage] = useState<number>();
  const [isNewCustomer, setIsNewCustomer] = useState<boolean>(initialIsNewCustomer || false);

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchValues || customerSearchDefaultValues
  );
  const isTransferProductFlow = !!transferableCardCustomerData;

  const loadCustomerList = useCallback(
    async (
      pageNumber?: number,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<CustomerTableRow[]> => {
      try {
        setLoadingTable(true);
        let customers;
        if (!transferableCardCustomerData) {
          const { birthDate, ...rest } = formValues;
          const { data } = await Customer.getCustomers({
            ...rest,
            birthDate: formatDate(birthDate),
            rowCountPerPage: 5,
            pageNumber: pageNumber || page,
            refresh: !pageNumber || forceRefresh,
          });
          customers = data.customerList.map((customer) => ({
            id: customer.customerId || '',
            columns: [
              `${customer.customerName} ${customer.customerSurname}`,
              customer.customerSecondName || '',
              `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
              customer.gender || '',
              `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
            ],
            customerNumber: customer.customerNumber,
          }));
          setWarning(data.warning);
          setHasNextPage(data.hasNextPage);
          setPage(pageNumber || 1);
        } else {
          customers = [transferableCardCustomerData];
          setPage(1);
        }
        setCustomerList(customers);
        setLoadingTable(false);

        return customers;
      } catch (error) {
        // TODO (ozgunbal): show error on modal or handle with error boundary

        return [];
      }
    },
    [page, searchFormValues, transferableCardCustomerData]
  );

  const loadPageItems = useCallback(
    async (pageNumber?: number): Promise<void> => {
      loadCustomerList(pageNumber);
    },
    [loadCustomerList]
  );

  useEffect(() => {
    const loadPreviousValues = async (): Promise<void> => {
      const customers = await loadCustomerList(initialCustomerPage, true);
      setSelectedCustomer(customers?.find((customer) => customer.id === selectedCustomerId));
      if (isTransferProductFlow) {
        setSearchFormValues(customerSearchValues || customerSearchDefaultValues);
        setSelectedCustomer(transferableCardCustomerData);
      }
    };

    if (
      !customerList &&
      !selectedCustomer &&
      (selectedCustomerId || !!initialIsNewCustomer || isTransferProductFlow)
    ) {
      loadPreviousValues();
    }
  }, [
    customerList,
    customerSearchValues,
    initialCustomerPage,
    initialIsNewCustomer,
    isTransferProductFlow,
    loadCustomerList,
    selectedCustomer,
    selectedCustomerId,
    transferableCardCustomerData,
  ]);

  const toggleModal = (): void => setIsModalOpen((state) => !state);

  const onSearchSubmit = (formData: SearchFormData): void => {
    setSearchFormValues(formData);
    clearCustomerSearchState();
    selectedCustomer && setSelectedCustomer(undefined);
    selectedCustomerPage && setSelectedCustomerPage(undefined);
    isNewCustomer && setIsNewCustomer(false);
    loadCustomerList(1, true, formData);
  };

  const handleNewCustomer = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setIsNewCustomer(target.checked);
    clearCustomerSearchState();
    selectedCustomer && setSelectedCustomer(undefined);
    selectedCustomerPage && setSelectedCustomerPage(undefined);
    warning && setWarning(undefined);
  };

  const handleSelectCustomer = (customer: CustomerTableRow | undefined): void => {
    setSelectedCustomer(customer);
    setSelectedCustomerPage(page);

    isNewCustomer && setIsNewCustomer(false);
    warning && setWarning(undefined);
  };

  const onCustomerSubmit = async (): Promise<void> => {
    dispatch({
      isNewCustomer: !selectedCustomer || isNewCustomer,
      customerId: selectedCustomer?.id,
      customerNumber: selectedCustomer?.customerNumber,
      customerSearchValues: searchFormValues,
      pageNumber: selectedCustomerPage,
    });
    const { data } = await IndividualPageValidation.validateIndividualPageDataForCustomerChange({
      customerId: isNewCustomer ? '' : selectedCustomer?.id,
    });

    if (data.result === 'OK') {
      goToNextStep(
        isNewCustomer ? undefined : selectedCustomer?.id,
        selectedCustomer?.customerNumber
      );
    }
  };

  const handleContinue = async (): Promise<void> => {
    if (!isNewCustomer && !selectedCustomer) {
      setWarning(t('cardEntry_customersearch_noSelectedCustomer_errorMessage'));
    } else if (customerList && customerList.length > 0 && !selectedCustomer) {
      toggleModal();
    } else {
      await onCustomerSubmit();
    }
  };

  const getConfirmMessage = (): string => {
    if (isNewCustomer) return t('cardEntry_customerInfo_newCustomerConfirm_text');

    return t('cardEntry_customerInfo_existingCustomerConfirm_text', {
      customerNumber: selectedCustomer?.customerNumber,
    });
  };

  const confirmMessage = getConfirmMessage();

  return (
    <Container>
      {isModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={<Markdown text={confirmMessage} />}
          onCancel={toggleModal}
          onSubmit={onCustomerSubmit}
          submitText={t('general_components_confirm_button')}
          title={t('general_confirmationPopUp_title')}
          type={ModalType.Confirmation}
        />
      ) : null}
      <Title>
        <Trans i18nKey="cardEntry_customerInfo_individual_subtitle" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_customerInfo_customerSearch_text" />
      </Subtitle>
      <CustomerSearchForm
        disabled={isTransferProductFlow || disablePage}
        formValues={searchFormValues}
        onSubmit={onSearchSubmit}
      />
      {customerList ? (
        <>
          <Subtitle>
            <Trans i18nKey="cardEntry_customerInfo_customerList_text" />
          </Subtitle>
          <Description>
            <Trans i18nKey="cardEntry_customerInfo_listExplanation_text" />
          </Description>
          <TableWrapper>
            <Table
              disabled={isNewCustomer || isTransferProductFlow || disablePage}
              emptyTableText={t('cardEntry_customerInfo_notFound_text')}
              hasNextPage={hasNextPage}
              headers={tableHeaders.map((head) => t(head))}
              items={customerList}
              loading={loadingTable}
              loadPageItems={loadPageItems}
              onSelect={handleSelectCustomer}
              pageNumber={page}
              selectedItem={selectedCustomer}
              widths={['20', '15', '30', '12', '23']}
              clearItemOnPageChange
              withRadio
            />
          </TableWrapper>
          {warning ? (
            <WarningText>
              <Markdown text={warning} />
            </WarningText>
          ) : null}

          <FormRow>
            <Checkbox
              disabled={isTransferProductFlow || disablePage}
              id="newCustomer"
              label={t('cardEntry_customerInfo_newCustomer_button')}
              onChange={handleNewCustomer}
              value={isNewCustomer}
            />
          </FormRow>
          <FormRow>
            <AsyncButton
              color={ButtonColor.Primary}
              disabled={loadingTable}
              onClick={handleContinue}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
            />
          </FormRow>
        </>
      ) : null}
    </Container>
  );
};

export default CustomerSearch;
